import React from 'react'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'

export interface SectionHeaderProps {
  /**
   * Title text to display
   */
  title: string
  /**
   * Optional description text
   */
  description?: string
  /**
   * Optional icon component
   */
  icon?: React.ReactNode
  /**
   * Shape of the icon container (matches reflection index badges when `circle`).
   * @default 'rounded'
   */
  iconContainerVariant?: 'rounded' | 'circle'
}

/**
 * SectionHeader - Header component with title, optional description, and optional icon
 */
export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  description,
  icon,
  iconContainerVariant = 'rounded'
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: '0px',
        gap: { xs: '12px', sm: '14px', md: '16px' },
        width: '100%',
        alignSelf: 'stretch'
      }}
    >
      {/* Icon (if provided) */}
      {icon && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0px',
            width: { xs: '40px', sm: '44px', md: '48px' },
            height: { xs: '40px', sm: '44px', md: '48px' },
            background: iconContainerVariant === 'circle' ? INTERAX_COLORS.sun20 : 'rgba(255, 171, 64, 0.2)',
            borderRadius: iconContainerVariant === 'circle' ? '50%' : '10px',
            border: iconContainerVariant === 'circle' ? `2px solid ${INTERAX_COLORS.sun}` : 'none',
            boxSizing: 'border-box',
            flex: 'none'
          }}
        >
          {icon}
        </Box>
      )}

      {/* Title and Description */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '0px',
          gap: '8px',
          flex: 1
        }}
      >
        {(() => {
          const words = title.trim().split(/\s+/)
          const firstWord = words.length > 1 ? words[0] : ''
          const rest = words.length > 1 ? words.slice(1).join(' ') : words[0] || ''

          return (
            <Box
              sx={{
                boxSizing: 'border-box',
                width: '100%',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}
            >
              {/* First word in white (if multiple words) */}
              {firstWord && (
                <Typography
                  sx={{
                    fontFamily: 'Lexend Deca',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    fontSize: { xs: '20px', sm: '24px', md: '26px' },
                    lineHeight: { xs: '28px', sm: '32px', md: '34px' },
                    color: INTERAX_COLORS.white,
                    margin: 0
                  }}
                >
                  {firstWord}
                </Typography>
              )}
              {/* Rest of name in gradient */}
              {rest && (
                <Typography
                  sx={{
                    fontFamily: 'Lexend Deca',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    fontSize: { xs: '20px', sm: '24px', md: '26px' },
                    lineHeight: { xs: '28px', sm: '32px', md: '34px' },
                    background: 'linear-gradient(360deg, #FFD196 -15.91%, #FFAB40 1.14%, #FFFFFF 56.35%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    textShadow: '0px 0px 10px rgba(0, 0, 0, 0.5), 0px 0px 20px rgba(255, 171, 64, 0.4)',
                    margin: 0
                  }}
                >
                  {rest}
                </Typography>
              )}
            </Box>
          )
        })()}
        {description && (
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.body3,
              color: INTERAX_COLORS.thunderGrey
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default SectionHeader
